import { useMemo, useState } from 'react'
import {
  Alert,
  Button,
  Form,
  Input,
  Modal,
  Select,
  Space,
  Tag,
  Tooltip,
  Typography,
} from 'antd'
import type { ColumnsType } from 'antd/es/table'
import { EyeOutlined, PlusOutlined } from '@ant-design/icons'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { queryKeys } from '@/shared/api/queryKeys'
import { translateError } from '@/shared/api/errors'
import { Can } from '@/shared/auth/Can'
import { usePermissions } from '@/shared/auth/usePermissions'
import { formatDateTime, utcTooltip } from '@/shared/lib/format'
import { PageHeader } from '@/shared/ui/PageHeader'
import { ClosedHiddenTag } from '@/shared/ui/table/ClosedHidden'
import { useClosedHidden } from '@/shared/ui/table/useClosedHidden'
import { STATE_ALL, STATE_OPEN_ONLY } from '@/shared/ui/table/closedFilter'
import { DataTable } from '@/shared/ui/table/DataTable'
import { RowActions } from '@/shared/ui/table/RowActions'
import { useTableUrlState } from '@/shared/ui/table/useTableUrlState'
import { LeadFilterBar } from '@/features/crm/CrmFilterBars'
import {
  countClosedLeads,
  createLead,
  fetchLeadPage,
  leadAssignBlock,
  leadEditPlan,
  leadTransition,
  revealLeadPhone,
  LEAD_NEXT,
  LEAD_SOURCES,
  LEAD_STATUSES,
  type Lead,
  type LeadSource,
  type LeadStatus,
} from '@/features/crm/crmApi'
import { actionReason } from '@/features/crm/actionReason'
import { CrmAssignModal } from '@/features/crm/CrmAssignModal'
import { LeadEditModal } from '@/features/crm/LeadEditModal'
// ⚠️ Telefon naqshi ABONENT modulidan olinadi, bu yerda qayta yozilmaydi
// (BUG-040 izohi formadagi maydon yonida).
import { PHONE_E164, normalizePhone } from '@/features/subscriber/subscriberApi'
import { LeadLostModal } from '@/features/crm/LeadLostModal'
import { LeadWonModal } from '@/features/crm/LeadWonModal'
import { LeadWonVenueModal } from '@/features/crm/LeadWonVenueModal'
import { submitOnValid } from '@/shared/lib/submitOnValid'

const STATUS_COLOR: Record<string, string> = {
  NEW: 'blue',
  CONTACTED: 'gold',
  QUALIFIED: 'geekblue',
  WON: 'green',
  LOST: 'default',
}

/**
 * TANASIZ o'tishlar: holat nomi → endpoint nomi.
 *
 * ⚠️ `WON` va `LOST` bu yerda ATAYLAB YO'Q — ular tugmani darhol
 * yubormaydi, MODAL ochadi (birinchisi abonent talab qiladi,
 * ikkinchisi sabab).
 *
 * ⚠️ Ilgari bu xarita `TRANSITION_PATH[to] ?? 'lost'` bilan
 * ishlatilardi va aynan shu zaxira qiymat eng og'ir nuqsonni bergan:
 * `WON` xaritada yo'q edi, ya'ni "Yutildi" tugmasi JIMGINA `/lost` ni
 * chaqirardi — lid yutilgan emas, yo'qotilgan bo'lib qolardi. Endi tip
 * `Record<LeadStatus, ...>` emas, aniq ikki kalitli obyekt va
 * zaxira qiymat umuman yo'q: xaritada bo'lmagan holat uchun kod
 * KOMPILYATSIYA bo'lmaydi, jimgina boshqa amalga aylanmaydi.
 */
const PLAIN_TRANSITION = {
  CONTACTED: 'contacted',
  QUALIFIED: 'qualify',
} as const

type PlainTransitionTarget = keyof typeof PLAIN_TRANSITION

/**
 * Lidlar — hali abonent bo'lmagan, lekin qiziqqan odamlar.
 *
 * <h2>⚠️ TELEFON NIQOBLANGAN VA U SHUNDAY QOLADI</h2>
 * Ro'yxat to'liq raqamni BERMAYDI. To'liq raqam uchun alohida huquq
 * ({@code crm.lead.reveal}), alohida so'rov va SABAB kerak — u audit
 * hodisasiga tushadi.
 *
 * <p>UI buni AYLANIB O'TMAYDI: ochilgan raqam React Query keshiga
 * yozilmaydi va faqat lokal holatda, faqat shu sahifa ochiq turganda
 * yashaydi. Aks holda "bir marta ochib, keyin hammaga ko'rsatish"
 * mumkin bo'lardi va huquq modeli qog'ozda qolardi.
 *
 * <p>⚠️ Sabab MAJBURIY va bo'sh qoldirib bo'lmaydi: auditda "kim
 * ochdi" yozuvi bor, lekin "nega ochdi" bo'lmasa, yozuvning qiymati
 * yo'q — tekshiruvchi baribir so'rab kelishga majbur bo'lardi.
 *
 * <h2>⚠️ YO'QOTILGAN LIDLAR SUKUT BO'YICHA KO'RINMAYDI (ADR-033)</h2>
 * Server `includeClosed` bayrog'ini oladi va uni panel HAR so'rovda
 * ochiq yuboradi ({@code leadListParams}) — serverdagi sukut qiymat
 * `true`, ya'ni yuborilmagani «hammasi» degani.
 *
 * <p>⚠️ «Yopiq» bu yerda FAQAT {@code LOST}. Yutilgan lid
 * ({@code WON}) ro'yxatda QOLADI: egasi qarori 2026-08-21 — sotuvchi
 * yutuqlari va konversiya aynan shu ro'yxatdan o'qiladi.
 *
 * <p>⚠️ Yagona yo'l — HOLAT FILTRI; alohida belgilagich yo'q. Filtr
 * yopilganni tanlaganda server bayroqni e'tiborsiz qoldiradi va
 * qatorlarni ROSTDAN qaytaradi ({@code LeadQuery.excludesClosed}).
 */
export function LeadsPage() {
  const { t } = useTranslation()
  const queryClient = useQueryClient()
  const [createOpen, setCreateOpen] = useState(false)
  const [revealing, setRevealing] = useState<Lead | null>(null)
  /** "Yutildi"/"Yo'qotildi" — ikkalasi ham modal orqali, sababi izohlarda */
  const [winning, setWinning] = useState<Lead | null>(null)
  /**
   * "Yutildi → OB'EKT" — ikkinchi NISHON, alohida oyna.
   *
   * ⚠️ `winning` bilan bitta holatga birlashtirilmadi: ular bir vaqtda
   * ochilmasligi kerak va ikki alohida qiymat buni tip darajasida
   * ta'minlaydi (bittasi tanlanganda ikkinchisi `null` qoladi).
   */
  const [winningVenue, setWinningVenue] = useState<Lead | null>(null)
  const [losing, setLosing] = useState<Lead | null>(null)
  /**
   * Tahrirlash va MAS'UL — ikki alohida oyna.
   *
   * ⚠️ Birlashtirilmadi: `PUT` to'liq almashtiradi va OCHILGAN telefonni
   * talab qiladi, `POST /{id}/assign` esa bitta ustunga tegadi va
   * `crm.lead.reveal` ni umuman talab qilmaydi. Uchta sabab
   * `CrmAssignModal` javadoc'ida.
   */
  const [editing, setEditing] = useState<Lead | null>(null)
  const [assigning, setAssigning] = useState<Lead | null>(null)
  const [error, setError] = useState<string | null>(null)

  /**
   * ⚠️ Ob'ekt nishoni uchun `venue.read` KERAK: oyna ro'yxatni
   * `/api/venues` dan oladi. Huquqsiz odamga bu bandni ko'rsatish
   * bo'sh tanlov oynasi bilan tugardi va sababi ko'rinmasdi
   * (`usePermissions` javadoc'i: bu QULAYLIK, xavfsizlik emas —
   * backend baribir qayta tekshiradi).
   */
  const { can } = usePermissions()
  const canPickVenue = can('venue.read')
  /*
    ⚠️ `crm.lead.update` ILGARI butun amallar guruhini o'ragan `<Can>` edi,
    ya'ni u HAR amal uchun shart. `RowActions.permission` esa YOKI bilan
    ishlaydi (`canAny`), shuning uchun VA sharti `hidden` orqali qoladi:
    «Yutildi» hamon `crm.lead.update` VA `crm.lead.convert` talab qiladi.
  */
  const canUpdateLead = can('crm.lead.update')
  /*
    ⚠️ Mas'ul tanlovi xodimlar ro'yxatini `identity` dan oladi
    (`fetchAllUsers`). `user.read` yo'q odamda so'rov 403 berardi va
    tanlov BO'SH ochilardi — shuning uchun band O'CHIQ bo'ladi va
    sababi yorlig'ida yoziladi (`canPickVenue` bilan bir xil oila,
    faqat u yerda ikkinchi yo'l bor edi, bu yerda yo'q).
  */
  const canPickAssignee = can('user.read')

  /**
   * Ochilgan raqamlar — FAQAT shu komponent umrida.
   *
   * ⚠️ React Query keshida EMAS va `localStorage` da ham emas: sahifa
   * yangilanganda yo'qolishi kerak. Vaucher kodlari bilan bir xil
   * qoida (`voucherApi.ts`).
   */
  const [revealed, setRevealed] = useState<Record<string, string>>({})

  const [form] = Form.useForm<{
    fullName: string
    phone: string
    companyName?: string
    source: LeadSource
    notes?: string
  }>()
  const [reasonForm] = Form.useForm<{ reason: string }>()

  /*
    Bo'sh holat IKKI XIL bo'lishi uchun jadval holati sahifaga ham kerak:
    "hali lid yo'q" va "filtr hech narsa topmadi" — bir xil matn bilan
    ko'rsatilsa, operator filtrni tozalash kerakligini bilmaydi va
    ro'yxatni bo'sh deb hisoblaydi. Holat manbai bitta — URL.
  */
  const { state: tableState, patch: patchTable, reset: resetTable } = useTableUrlState()
  const isFiltered = tableState.search !== '' || Object.keys(tableState.filters).length > 0

  /*
    ⚠️ SUKUT KO'RINISH YO'QOTILGAN LIDLARSIZ (ADR-033) va bu torayish
    EKRANDA aytiladi. `WON` esa ro'yxatda QOLADI — sabab `crmApi`
    dagi `LEAD_CLOSED_STATUS` izohida.

    ⚠️ Sanoq FAQAT sukut ko'rinishda so'raladi: holat tanlanganda
    yashiringan qator yo'q va so'rov ham keraksiz.
  */
  const closedFilterValue = tableState.filters['status']?.[0] ?? STATE_OPEN_ONLY
  const hiddenCounts = useClosedHidden<Lead>({
    queryKey: queryKeys.crm.leadList,
    state: tableState,
    fetchPage: fetchLeadPage,
    countClosed: countClosedLeads,
    enabled: closedFilterValue === STATE_OPEN_ONLY,
  })
  const showAllStates = () =>
    patchTable({ page: 1, filters: { ...tableState.filters, status: [STATE_ALL] } })

  /**
   * Holat ro'yxati USTUN filtri uchun ham kerak.
   *
   * ⚠️ Usiz filtr sahifa almashtirilishida JIMGINA tushib qolardi:
   * `DataTable.handleChange` filtrlarni antd bergan ro'yxatdan qayta
   * quradi, antd esa faqat `filters` e'lon qilgan ustunlarni biladi.
   * Ya'ni operator 2-sahifaga o'tishi bilan yopilganlar yana yashirin
   * bo'lardi va hech qanday xato ko'rinmasdi (`VenuesPage` dagi bir xil
   * qaror).
   */
  const statusOptions = useMemo(
    () => [
      { value: STATE_OPEN_ONLY, label: t('dataTable.stateOpenOnly') },
      ...LEAD_STATUSES.map((s) => ({ value: s, label: t(`crm.leadStatus.${s}`) })),
      { value: STATE_ALL, label: t('dataTable.stateAll') },
    ],
    [t],
  )

  const refresh = () => void queryClient.invalidateQueries({ queryKey: queryKeys.crm.leadList })

  const transition = useMutation({
    mutationFn: ({ id, to }: { id: string; to: PlainTransitionTarget }) =>
      leadTransition(id, PLAIN_TRANSITION[to]),
    onSuccess: refresh,
    onError: (err) => setError(translateError(err, t)),
  })

  const create = useMutation({
    mutationFn: (values: {
      fullName: string
      phone: string
      companyName?: string
      source: LeadSource
      notes?: string
    }) => createLead(values),
    onSuccess: () => {
      setCreateOpen(false)
      form.resetFields()
      refresh()
    },
    // ⚠️ `onError` ATAYLAB yo'q — xato OYNANING ICHIDA (yuqoridagi izoh).
  })

  const reveal = useMutation({
    mutationFn: ({ id, reason }: { id: string; reason: string }) => revealLeadPhone(id, reason),
    onSuccess: (lead) => {
      if (lead.phone) setRevealed((prev) => ({ ...prev, [lead.id]: lead.phone as string }))
      setRevealing(null)
      reasonForm.resetFields()
    },
    // ⚠️ `onError` ATAYLAB yo'q — xato OYNANING ICHIDA. `transition` esa
    // oynasiz amal, uning xatosi sahifa bannerida to'g'ri joyda qoladi.
  })

  /** Bekor qilib chiqish — xato xabari ham qolmasin */
  const closeReveal = () => {
    reveal.reset()
    setRevealing(null)
  }

  const closeCreate = () => {
    create.reset()
    setCreateOpen(false)
  }

  // ⚠️ Tartib raqami ustuni bu yerda YO'Q — uni `DataTable` o'zi qo'shadi
  // va sahifa bo'yicha uzluksiz sanaydi. Qo'lda yozilgani server
  // sahifasi (0 dan) bilan jadval sahifasi (1 dan) farqini har sahifada
  // qaytadan tarjima qilishni talab qilardi.
  const columns = useMemo<ColumnsType<Lead>>(() => [
    {
      key: 'name',
      title: t('crm.lead.fullName'),
      width: 220,
      render: (_, row) => (
        <Space direction="vertical" size={0}>
          <Typography.Text strong>{row.fullName}</Typography.Text>
          {row.companyName ? (
            <Typography.Text type="secondary" style={{ fontSize: 12 }}>
              {row.companyName}
            </Typography.Text>
          ) : null}
        </Space>
      ),
    },
    {
      key: 'phone',
      title: t('crm.lead.phone'),
      width: 210,
      render: (_, row) => {
        const open = revealed[row.id]
        if (open) {
          return (
            <Space size={6}>
              <Typography.Text copyable>{open}</Typography.Text>
              <Tooltip title={t('crm.lead.revealedHint')}>
                <Tag color="orange">{t('crm.lead.revealed')}</Tag>
              </Tooltip>
            </Space>
          )
        }
        return (
          <Space size={6}>
            <Typography.Text type="secondary">{row.phone ?? '—'}</Typography.Text>
            {/*
              ⚠️ RAQAMSIZ LIDDA TUGMA CHIZILMAYDI (jonli Staging,
              2026-08-24): ustunda «—» turgan qatorlarda ham «Raqamni
              ochish» ko'rinardi. Ochadigan narsa yo'q — bosilsa sabab
              so'ralib, natijada bo'sh qiymat qaytardi va operator
              «ochilmadi, nimadir buzuq» deb o'ylardi. Bo'sh satr ham
              «yo'q» (`ContactCard.phoneOnScreen` bilan bir xil shart).
            */}
            {(row.phone ?? '').trim() !== '' ? (
              <Can permission="crm.lead.reveal">
                <Button
                  size="small"
                  type="link"
                  icon={<EyeOutlined />}
                  onClick={() => setRevealing(row)}
                >
                  {t('crm.lead.reveal')}
                </Button>
              </Can>
            ) : null}
          </Space>
        )
      },
    },
    {
      // ⚠️ Ustun kaliti FILTR kaliti bilan bir xil va `filters` MAJBURIY —
      // sabab `statusOptions` izohida.
      key: 'status',
      title: t('subscribers.columns.status'),
      width: 130,
      filters: statusOptions.map((o) => ({ text: o.label, value: o.value })),
      filterMultiple: false,
      render: (_, row) => (
        <Tag color={STATUS_COLOR[row.status]}>{t(`crm.leadStatus.${row.status}`)}</Tag>
      ),
    },
    {
      key: 'source',
      title: t('crm.lead.source'),
      width: 140,
      render: (_, row) => t(`crm.leadSource.${row.source}`),
    },
    {
      key: 'lastContacted',
      title: t('crm.lead.lastContacted'),
      width: 170,
      /*
        ⚠️ Aloqa qilinmagani "—" emas, ANIQ MATN: yangi lid bilan hali
        gaplashilmagani sotuvdagi eng muhim signal va uni bo'sh katak
        qilib qoldirish "ma'lumot yo'q" bo'lib o'qilardi.
      */
      render: (_, row) =>
        row.lastContactedAt ? (
          formatDateTime(row.lastContactedAt)
        ) : (
          <Typography.Text type="warning">{t('crm.lead.neverContacted')}</Typography.Text>
        ),
    },
    {
      /*
        ⚠️ Ustun QO'SHILDI — ro'yxat tartibi KO'RINSIN uchun.

        Server `ORDER BY created_at DESC` bilan qaytaradi, ya'ni eng
        yangisi birinchi. Sana ustunsiz bu tartib ko'rinmasdi va
        operator ro'yxat qanday saralanganini taxmin qilishga majbur
        edi. `sorter` ATAYLAB yo'q: `LeadQuery` saralash parametrini
        qabul qilmaydi va faqat joriy sahifani saralash yolg'on tartib
        berardi.
      */
      key: 'createdAt',
      title: t('common.createdAt'),
      width: 170,
      render: (_, row) => (
        <Tooltip title={utcTooltip(row.createdAt)}>{formatDateTime(row.createdAt)}</Tooltip>
      ),
    },
    {
      key: 'actions',
      title: '',
      width: 56,
      align: 'center',
      // ⚠️ `fixed: 'right'` sababi `TicketsPage` da yozilgan: gorizontal
      // scroll bo'lgan jadvalda amallar ustuni ekrandan chiqib ketadi.
      fixed: 'right',
      render: (_, row) => {
        const next = LEAD_NEXT[row.status as LeadStatus] ?? []
        /*
          ⚠️ Zaxira qiymat O'RNIGA aniq tekshiruv: ruxsat etilmagan
          o'tish UMUMAN chizilmaydi. Ilgari bu yerda `?? 'lost'` turardi
          va noma'lum holat jimgina "yo'qotildi" ga aylanardi.

          `next` bo'sh bo'lsa (WON/LOST) hamma band yashirinadi va
          `RowActions` menyuni umuman chizmaydi.
        */
        const allowed = (to: LeadStatus) => canUpdateLead && next.includes(to)
        /*
          ⚠️ TAHRIR TO'SIG'I — server qoidasi va niqoblangan telefon.
          Uchala sabab `leadEditPlan` javadoc'ida; band YASHIRILMAYDI,
          O'CHADI va sababi yorlig'ida turadi.
        */
        const editBlock = leadEditPlan(row, revealed[row.id]).block
        const assignBlock = canPickAssignee ? leadAssignBlock(row) : 'noDirectory'
        return (
          <RowActions
            items={[
              {
                key: 'edit',
                label: actionReason(
                  t('common.edit'),
                  editBlock === null ? null : t(`crm.leadEdit.blocked.${editBlock}`),
                ),
                onClick: () => setEditing(row),
                disabled: editBlock !== null,
                permission: 'crm.lead.update',
              },
              {
                key: 'assign',
                label: actionReason(
                  t('crm.assign.action'),
                  assignBlock === null ? null : t(`crm.assign.blocked.${assignBlock}`),
                ),
                onClick: () => setAssigning(row),
                disabled: assignBlock !== null,
                permission: 'crm.lead.update',
              },
              /*
                ⚠️ Uch xil amal, uch xil yo'l — va ular BIR XIL bandga
                o'xshaydi. Aynan shu o'xshashlik nuqsonni yashirgan edi:
                "Yutildi" ham oddiy o'tish deb hisoblanib, umumiy
                funksiyaga berilardi.

                · WON  — nishon TALAB QILADI, shuning uchun modal;
                · LOST — sabab talab qiladi, shuning uchun modal;
                · qolgani — tanasiz o'tish, darhol yuboriladi.
              */
              {
                key: 'CONTACTED',
                label: t('crm.leadAction.CONTACTED'),
                onClick: () => transition.mutate({ id: row.id, to: 'CONTACTED' }),
                loading: transition.isPending,
                // ⚠️ Ajratuvchi HAR o'tishda: `LEAD_NEXT` bo'yicha
                // CONTACTED/QUALIFIED/WON dan bir vaqtda AYNAN BITTASI
                // ko'rinadi, ya'ni ikkinchi chiziq paydo bo'lmaydi.
                divider: true,
                hidden: !allowed('CONTACTED'),
              },
              {
                key: 'QUALIFIED',
                label: t('crm.leadAction.QUALIFIED'),
                onClick: () => transition.mutate({ id: row.id, to: 'QUALIFIED' }),
                loading: transition.isPending,
                divider: true,
                hidden: !allowed('QUALIFIED'),
              },
              /*
                ⚠️ IKKI NISHON — va nishonsiz "yutildi" MAVJUD EMAS.
                Bu kontroller qarori emas, BAZA qarori:

                  CONSTRAINT lead_won_consistent CHECK (
                      status = 'WON' AND converted_at IS NOT NULL
                      AND (converted_subscriber_id IS NOT NULL)
                       <> (converted_venue_id IS NOT NULL))

                Ya'ni yutilgan lid AYNAN BITTA nishonga — abonent YOKI
                ob'ektga — bog'langan bo'lishi shart. Shuning uchun band
                darhol yubormaydi, MODAL ochadi.

                ⚠️ `venue.read` yo'q odamga nishon tanlovi ko'rsatilmaydi:
                oyna ro'yxatni `/api/venues` dan oladi va bo'sh tanlov
                bilan tugardi. Unga bitta — abonent — yo'li qoladi, ya'ni
                ilgarigi xulq.
              */
              {
                key: 'WON',
                label: t('crm.leadAction.WON'),
                onClick: () => setWinning(row),
                permission: 'crm.lead.convert',
                divider: true,
                hidden: !allowed('WON') || canPickVenue,
              },
              {
                key: 'WON_subscriber',
                label: `${t('crm.leadAction.WON')}: ${t('crm.leadWonTarget.subscriber')}`,
                onClick: () => setWinning(row),
                permission: 'crm.lead.convert',
                divider: true,
                hidden: !allowed('WON') || !canPickVenue,
              },
              {
                key: 'WON_venue',
                label: `${t('crm.leadAction.WON')}: ${t('crm.leadWonTarget.venue')}`,
                onClick: () => setWinningVenue(row),
                permission: 'crm.lead.convert',
                hidden: !allowed('WON') || !canPickVenue,
              },
              {
                key: 'LOST',
                label: t('crm.leadAction.LOST'),
                onClick: () => setLosing(row),
                danger: true,
                divider: true,
                hidden: !allowed('LOST'),
              },
            ]}
          />
        )
      },
    },
  ], [t, revealed, canUpdateLead, canPickVenue, canPickAssignee, transition, statusOptions])

  return (
    <>
      <PageHeader
        title={t('crm.lead.title')}
        description={t('crm.lead.subtitle')}
        extra={
          <Can permission="crm.lead.create">
            <Button type="primary" icon={<PlusOutlined />} onClick={() => setCreateOpen(true)}>
              {t('crm.lead.create')}
            </Button>
          </Can>
        }
      />

      {error ? (
        <Alert
          type="error"
          showIcon
          closable
          message={error}
          onClose={() => setError(null)}
          style={{ marginBottom: 16 }}
        />
      ) : null}

      {/*
        ⚠️ Ro'yxat XATOSI uchun alohida banner endi YO'Q — uni `DataTable`
        o'zi chizadi va sababini (`translateError`) ham, "qayta urinish"
        tugmasini ham beradi. Ikkinchi banner bir xil xatoni ikki marta
        ko'rsatardi.

        ⚠️ SAHIFALASH, QIDIRUV VA FILTR — SERVERDA (`fetchLeadPage`).
        `LeadQuery` `page`/`size`/`search`/`status`/`source` ni haqiqatdan
        qabul qiladi, ya'ni bu yerda to'liq ro'yxat olib mijozda kesish
        yo'q.
      */}
      <DataTable<Lead>
        queryKey={queryKeys.crm.leadList}
        fetchPage={fetchLeadPage}
        columns={columns}
        rowKey={(row) => row.id}
        tableId="crm-leads"
        stickyFirstColumn
        searchable
        searchPlaceholder={t('crm.lead.searchPlaceholder')}
        toolbarExtra={
          <Space wrap size="small">
            <LeadFilterBar />
            <ClosedHiddenTag counts={hiddenCounts} onShowAll={showAllStates} />
          </Space>
        }
        scrollX={1090}
        emptyVariant="no-data"
        {...(isFiltered
          ? {
              emptyTitle: t('dataTable.emptyFiltered'),
              emptyDescription: t('dataTable.emptyFilteredHint'),
              emptyAction: { label: t('dataTable.resetFilters'), onClick: resetTable },
            }
          : {
              emptyTitle: t('crm.lead.emptyTitle'),
              /*
                ⚠️ Bo'sh ro'yxat «lid yo'q» degani EMAS: yo'qotilganlar
                sukut bo'yicha yashirin. Ikkinchi yo'l ko'rsatilmasa
                operator ular butunlay o'chgan deb o'ylardi.
              */
              ...(hiddenCounts && hiddenCounts.hidden > 0
                ? {
                    emptyDescription: t('dataTable.closedHiddenHint'),
                    emptyAction: { label: t('dataTable.stateAll'), onClick: showAllStates },
                  }
                : {
                    emptyDescription: t('crm.lead.emptyHint'),
                    ...(can('crm.lead.create')
                      ? {
                          emptyAction: {
                            label: t('crm.lead.create'),
                            onClick: () => setCreateOpen(true),
                          },
                        }
                      : {}),
                  }),
            })}
      />

      {/* --- Yutildi: mavjud abonentga bog'lash YOKI yangisini ochish --- */}
      <LeadWonModal
        lead={winning}
        onCancel={() => setWinning(null)}
        onDone={() => {
          setWinning(null)
          refresh()
        }}
      />

      {/* --- Yutildi: ikkinchi NISHON — mavjud OB'EKTGA bog'lash --- */}
      <LeadWonVenueModal
        lead={winningVenue}
        onCancel={() => setWinningVenue(null)}
        onDone={() => {
          setWinningVenue(null)
          refresh()
        }}
      />

      {/* --- Yo'qotildi: sabab MAJBURIY (backend usiz rad etadi) --- */}
      <LeadLostModal
        lead={losing}
        onCancel={() => setLosing(null)}
        onDone={() => {
          setLosing(null)
          refresh()
        }}
      />

      {/* --- Tavsifni to'g'rilash: `PUT` TO'LIQ ALMASHTIRADI --- */}
      <LeadEditModal
        lead={editing}
        fullPhone={editing === null ? undefined : revealed[editing.id]}
        onCancel={() => setEditing(null)}
        onDone={(phone) => {
          /*
            ⚠️ Ochilgan raqamlar xaritasi HAM yangilanadi: server javobi
            yana NIQOBLANGAN keladi (`Lead.masked()`), ya'ni xarita
            yangilanmasa saqlangan raqam eskisi bo'lib qolardi va
            keyingi tahrir eski qiymatni qaytarib yozardi.
          */
          if (editing !== null) {
            const id = editing.id
            setRevealed((prev) => {
              const next = { ...prev }
              if (phone === null) delete next[id]
              else next[id] = phone
              return next
            })
          }
          setEditing(null)
          refresh()
        }}
      />

      {/* --- Mas'ul: ALOHIDA amal, sabablari `CrmAssignModal` da --- */}
      <CrmAssignModal
        kind="lead"
        target={
          assigning === null
            ? null
            : { id: assigning.id, title: assigning.fullName, assignedTo: assigning.assignedTo }
        }
        onCancel={() => setAssigning(null)}
        onDone={() => {
          setAssigning(null)
          refresh()
        }}
      />

      {/* --- Telefonni ochish: sabab MAJBURIY --- */}
      <Modal
        open={revealing !== null}
        title={t('crm.lead.revealTitle')}
        onCancel={closeReveal}
        onOk={() =>
          submitOnValid(reasonForm, (values) => {
            if (revealing) reveal.mutate({ id: revealing.id, reason: values.reason })
          })
        }
        confirmLoading={reveal.isPending}
        okText={t('crm.lead.reveal')}
        cancelText={t('common.cancel')}
        destroyOnHidden
      >
        <Alert
          type="warning"
          showIcon
          style={{ marginBottom: 16 }}
          message={t('crm.lead.revealWarn')}
          description={t('crm.lead.revealWarnHint')}
        />
        {reveal.isError ? (
          <Alert
            type="error"
            showIcon
            style={{ marginBottom: 16 }}
            message={translateError(reveal.error, t)}
          />
        ) : null}
        <Form form={reasonForm} layout="vertical" preserve={false}>
          <Form.Item
            name="reason"
            label={t('crm.lead.revealReason')}
            rules={[{ required: true, message: t('crm.lead.revealReasonRequired') }]}
          >
            <Input.TextArea rows={3} autoFocus />
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        open={createOpen}
        title={t('crm.lead.create')}
        onCancel={closeCreate}
        onOk={() => submitOnValid(form, (values) => create.mutate(values))}
        confirmLoading={create.isPending}
        okText={t('operation.create')}
        cancelText={t('common.cancel')}
        destroyOnHidden
      >
        {create.isError ? (
          <Alert
            type="error"
            showIcon
            style={{ marginBottom: 16 }}
            message={translateError(create.error, t)}
          />
        ) : null}

        <Form form={form} layout="vertical" preserve={false}>
          <Form.Item
            name="fullName"
            label={t('crm.lead.fullName')}
            rules={[{ required: true, message: t('crm.lead.fullNameRequired') }]}
          >
            <Input autoFocus />
          </Form.Item>
          {/*
            ⚠️ SHAKL TEKSHIRUVI QO'SHILDI (tester-2, BUG-040).

            O'lchangan holat (Staging, 2026-08-16): bu yerda faqat
            `{ required: true }` turardi va serverda ham naqsh yo'q edi.
            Zond `abc` kiritdi — maydon ostida BIRORTA xato chiqmadi va
            forma `POST /api/crm/leads` ni yubordi. Lid esa aynan qayta
            bog'lanish uchun ochiladi: yaroqsiz raqam bilan u sotuvchi
            uchun bo'sh yozuv, lekin ro'yxatda to'liq ko'rinadi.

            ⚠️ NAQSH ABONENT FORMASINIKI BILAN AYNAN BITTA
            (`subscriberApi.PHONE_E164` + `normalizePhone`) — bu yerda
            YANGI naqsh yozilmadi. Ikki nusxa naqsh ajrashganda bitta
            panelda bitta raqam bir joyda o'tib, boshqasida rad etilardi.

            ⚠️ Oddiy `pattern` YETMAYDI: operator raqamni odatda
            `+998 90 123 45 67` ko'rinishida yozadi. Backend uni tozalab
            qabul qiladi (`Phones.normalize`), shuning uchun panel ham
            AVVAL tozalaydi, keyin tekshiradi — aks holda panel serverdan
            qattiqroq bo'lib qolardi.

            ⚠️ Bo'sh qiymat bu yerda `required` qoidasiga tushadi, shakl
            qoidasiga emas: ikkalasi bitta xabarga qo'shilsa operator
            «raqamni kiriting» o'rniga «format noto'g'ri» ni ko'rardi.
          */}
          <Form.Item
            name="phone"
            label={t('crm.lead.phone')}
            rules={[
              { required: true, message: t('crm.lead.phoneRequired') },
              {
                validator: (_, value: string | undefined) => {
                  const clean = normalizePhone(value)
                  if (clean === null || PHONE_E164.test(clean)) return Promise.resolve()
                  return Promise.reject(new Error(t('crm.lead.phoneFormat')))
                },
              },
            ]}
          >
            <Input inputMode="tel" autoComplete="off" />
          </Form.Item>
          <Form.Item name="companyName" label={t('crm.lead.company')}>
            <Input />
          </Form.Item>
          <Form.Item
            name="source"
            label={t('crm.lead.source')}
            initialValue="PHONE"
            rules={[{ required: true }]}
          >
            <Select
              options={LEAD_SOURCES.map((s) => ({ value: s, label: t(`crm.leadSource.${s}`) }))}
            />
          </Form.Item>
          <Form.Item name="notes" label={t('crm.lead.notes')}>
            <Input.TextArea rows={3} />
          </Form.Item>
        </Form>
      </Modal>
    </>
  )
}
